// Side-effect import: <std-dsh-turn-row> must be registered before this
// hydrates one, and the class is otherwise referenced only as a type.
import "./row";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";
import type { DshTurnRow } from "./row";

/** `thought`, in the dsh register — one row that says `Think`, and the
 * reasoning behind it.
 *
 * ## The label is `Think` and it never says anything else
 *
 * The native skin writes "Thought for 12s" once the duration is known,
 * because a document has room for a fact and a reader auditing a decision
 * wants it. dsh does not, and the difference is not an oversight: its
 * transcript is a column of labels a reader has learned the SHAPE of —
 * `Think`, `Run`, `Read`, `Edit` — and a label whose width changes with its
 * content is a label the eye has to re-read on every row. `summary` is still
 * ACCEPTED (the contract carries it, and a skin that refused it would not be
 * a skin), and not drawn. A `durationMs` this skin once tolerated is gone:
 * the contract never carried it, nothing drew it, and a prop that exists
 * only to be ignored is drift the two-way contract check now rejects.
 *
 * ## The collapsed line is a ticker while the thinking runs
 *
 * Ported from the native skin, mechanic for mechanic, because it is the same
 * mechanic and a second implementation of it would be a fork. While `active`
 * the subject carries the LATEST non-blank line of the reasoning, clipped at
 * its right edge and scrolled to its tail; every SSE bind pushes it along, so
 * the text runs leftward at the stream's own rate with no scripted animation
 * at all.
 *
 * The scroll is set per BIND and never on a rAF loop. A loop is a second
 * clock running against the stream's, and it keeps running on a row whose
 * turn ended — which is the failure a ticker has to not have.
 *
 * Settled, the line shows the FIRST non-blank line and ellipsises: once the
 * thinking is over the reader is skimming, and the opening of a thought is
 * what tells them whether to open it. The tail was only ever interesting
 * because it was the present tense. */
@component({ name: "std-dsh-turn-thought" })
export class DshTurnThought extends LonaWebComponent {
  private $row: DshTurnRow = this.hydrate("row");

  /** The scrolling half of the collapsed line, rebuilt on every bind — the
   * row clears and re-renders its whole tree when bound, so a reference kept
   * across binds would point at a detached node. Held only so the scroll can
   * be applied after the accordion beneath has appended it. */
  private $line: Option<HTMLElement> = null;

  /** Whether that line is a ticker (tail visible) or a preview (head
   * visible). Written on EVERY bind, never accumulated — a pooled row
   * arriving from a live turn must not keep scrolling a settled one. */
  private ticking = false;

  static makeWith(props: DshTurnThought.Props): DshTurnThought {
    return (DshTurnThought.make() as DshTurnThought).bind(props);
  }

  bind(props: DshTurnThought.Props): this {
    this.ticking = props.active ?? false;
    const line = this.ticking
      ? DshTurnThought.lastLine(props.text)
      : DshTurnThought.firstLine(props.text);
    const $line = DshTurnThought.buildLine(line, this.ticking);
    this.$line = $line;
    void this.$row.bind({
      kind: "thought",
      label: DshTurnThought.LABEL,
      subject: $line,
      // Live reasoning takes the row's glare rather than a mark of its own.
      // "Still thinking" is a fact about the whole row, and the gutter is
      // already spending its 16px on the kind.
      state: this.ticking ? "running" : "idle",
      body: DshTurnThought.buildBody(props.text),
      bodyIndent: "prose",
      expanded: props.expanded ?? false,
      // Open, the preview line is the first line of the body directly under
      // it. Two copies of one sentence, six pixels apart.
      keepSubjectWhenOpen: false,
    });
    this.scrollLine();
    return this;
  }

  /** The first bind almost always runs BEFORE the row is in the document —
   * `makeWith` builds it and the caller appends it — and an element outside a
   * laid-out tree reports scrollWidth 0, so that bind's scroll is a no-op. On
   * a live turn the next chunk fixes it 2s later; on a row that arrives
   * already settled there is no next chunk, which is what this is for. */
  connectedCallback(): void {
    super.connectedCallback();
    this.scrollLine();
  }

  /** Tail for a ticker, head for a preview. Assigning past the maximum is how
   * "scroll to the end" is spelled — the browser clamps to
   * scrollWidth - clientWidth — so this needs no measurement of its own. */
  private scrollLine(): void {
    const $line = this.$line;
    if ($line == null) return;
    $line.scrollLeft = this.ticking ? $line.scrollWidth : 0;
  }

  /** The first non-blank line, for a settled row: reasoning routinely opens
   * with a blank line or a bare heading, and a preview that renders empty is
   * worse than no preview at all. */
  private static firstLine(text: string): string {
    return DshTurnThought.lines(text)[0] ?? "";
  }

  /** The last non-blank line, for a live one — the sentence the model is
   * writing right now, which is the only line of a running thought a reader
   * is asking about. */
  private static lastLine(text: string): string {
    const lines = DshTurnThought.lines(text);
    return lines[lines.length - 1] ?? "";
  }

  private static lines(text: string): string[] {
    return text.split("\n").map((line) => line.trim()).filter((line) => line !== "");
  }

  /** The moving half of the row.
   *
   * `overflow: hidden` is what makes it SCROLLABLE at all — scrollLeft on an
   * overflow-visible box is pinned to zero — and the two ends differ in one
   * declaration: a ticker CLIPS, so the tail runs off the right edge mid-word
   * the way a terminal does, while a settled preview ellipsises, because an
   * ellipsis is a promise that there is more behind the row.
   *
   * Styled inline because it travels through two shadow roots (this one's, the
   * row's, the accordion's) and a sheet declared here reaches none of them.
   * Typography's sheet IS in every shadow root, so the ramp class resolves and
   * only the values travel. */
  private static buildLine(line: string, active: boolean): HTMLElement {
    const $line = Typography.small(line);
    Object.assign($line.style, {
      // LITERAL zeros, not --margin-top. Typography trims a text block with
      // negative margins computed from the cap height, so the variable is
      // never read; flex lays out MARGIN boxes, and an untrimmed run makes
      // this row participate as something other than its own ink.
      marginTop: "0",
      marginBottom: "0",
      display: line === "" ? "none" : "",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: active ? "clip" : "ellipsis",
    });
    return $line;
  }

  /** The reasoning, whole, in the row's own word column.
   *
   * No max-height and no scroll of its own, which is the one place this
   * deliberately parts from the tool body below it. A tool's output is
   * machine text a reader samples; reasoning is prose a reader READS, and a
   * 260px window on an argument is a window the reader has to drag. The row
   * is collapsed by default — that is already the height control. */
  private static buildBody(text: string): HTMLElement {
    const $body = Typography.small(text);
    Object.assign($body.style, {
      color: "var(--text-sec, #6a7186)",
      whiteSpace: "pre-wrap",
      overflowWrap: "anywhere",
    });
    $body.style.setProperty("--line-height-ratio", "1.55");
    return $body;
  }

  static $styles = [
    css`
      /* width, not shrink-to-fit: a disclosure must only ever change
         HEIGHT. Collapsed, a shrink-to-fit host is the width of one short
         line, and any centring parent moves the whole row when it opens. */
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-dsh-turn-row id="row"></std-dsh-turn-row>
  `;
}

export namespace DshTurnThought {
  /** One word, always. See the note above on why it never becomes a
   * duration. */
  export const LABEL = "Think";

  export type Props = {
    text: string;
    /** Accepted and not drawn — this skin's label is fixed. */
    summary?: string;
    expanded?: boolean;
    /** Still streaming: the row's subject tracks the tail of `text` instead
     * of previewing its head, and the row takes the running glare. */
    active?: boolean;
  };
}
